// The library's sections, scope, filters, the upload sheet's rules and the full-screen player's navigation, as pure
// functions (no DOM). Unit-tested in library.test.ts.
//
// v4 (spec-v4-structure-and-stage.md 3.3, 5.1, 7.1-7.4; ticket V4-A2): a library at every level. A scope is
// "account" (Everything), "channel:<id>", "collection:<id>" or "project:<id>", the form GET /api/library?scope= takes
// and the tree screens link with. The api filters by scope; inScope() is the same rule for the mock and the tests.
// Roles become sections (a product photo, a place, a theme song, an intro clip each have their own shelf).
import { fmtBytes } from "./download";
import type { Asset } from "./types";

// ------------------------------------------------------------------ sections (roles become sections)

export type LibSection = "ads" | "clips" | "intros" | "music" | "sfx" | "voices" | "products" | "places" | "props" | "photos" | "logos" | "more";
export const SECTIONS: { id: LibSection; label: string; empty: string }[] = [
  { id: "ads", label: "Videos", empty: "Finished videos land here after a render, every version kept." },
  { id: "clips", label: "Clips", empty: "Each scene's animated clip is kept here once it's made. Add your own clips too." },
  { id: "intros", label: "Intros and outros", empty: "Add a short clip to play before or after every video." },
  { id: "music", label: "Music", empty: "Songs, theme music and music beds live here." },
  { id: "sfx", label: "Sound effects", empty: "Add a sound effect and your Director can put it on a scene." },
  { id: "voices", label: "Voices", empty: "Voiceovers and the narrators you've picked live here." },
  { id: "products", label: "Products", empty: "Add a product photo and your Director puts it in the shot." },
  { id: "places", label: "Places", empty: "Photos of places your videos happen in." },
  { id: "props", label: "Props", empty: "Things that show up in your scenes." },
  { id: "photos", label: "Photos and drawings", empty: "Your own photos and drawings." },
  { id: "logos", label: "Logos", empty: "Your logos, for the end card and the brand." },
  { id: "more", label: "Frames and looks", empty: "Scene frames, saved looks and characters." },
];

const tagged = (a: Asset, t: string) => a.tags.some((x) => x.toLowerCase() === t);
const roleOf = (a: Asset) => a.role ?? (typeof a.meta.role === "string" ? a.meta.role : null);

export function sectionOf(a: Asset): LibSection {
  const role = roleOf(a);
  switch (a.kind) {
    case "video":
      if (role === "intro" || role === "outro") return "intros";
      if (a.meta.sceneId || a.meta.scene_id || a.meta.clip === true || role === "clip" || tagged(a, "clip") || a.origin === "upload") return "clips";
      return "ads";
    case "music":
      return "music";
    case "sfx":
      return "sfx";
    case "voice":
    case "voice_preset":
      return "voices";
    case "logo":
      return "logos";
    case "image":
      if (role === "product" || tagged(a, "product") || a.meta.product === true) return "products";
      if (role === "location") return "places";
      if (role === "prop") return "props";
      if (role === "photo" || role === "drawing" || a.origin === "upload") return "photos";
      return "more";
    default:
      return "more";
  }
}

/** Items grouped in section order (empty sections left out). */
export function groupSections(items: Asset[]): { section: (typeof SECTIONS)[number]; items: Asset[] }[] {
  return SECTIONS.map((section) => ({ section, items: items.filter((a) => sectionOf(a) === section.id) })).filter((g) => g.items.length);
}

/** The section chips worth showing: the ones with something in them, plus the one picked (so it can be unpicked). */
export function sectionChips(items: Asset[], picked: LibSection | ""): (typeof SECTIONS)[number][] {
  const have = new Set(items.map(sectionOf));
  return SECTIONS.filter((s) => have.has(s.id) || s.id === picked);
}

// ------------------------------------------------------------------ scope

export type ScopeLevel = "account" | "channel" | "collection" | "project";
export type LibScope = { level: "account" } | { level: Exclude<ScopeLevel, "account">; id: string };
export const EVERYTHING: LibScope = { level: "account" };

/** "channel:<id>" -> {level, id}; anything unreadable is Everything. */
export function parseScope(s: string | null | undefined): LibScope {
  const m = /^(channel|collection|project):(.+)$/.exec((s ?? "").trim());
  return m ? { level: m[1] as "channel" | "collection" | "project", id: m[2]! } : EVERYTHING;
}
export const scopeKey = (s: LibScope) => (s.level === "account" ? "account" : `${s.level}:${s.id}`);
export const sameScope = (a: LibScope, b: LibScope) => scopeKey(a) === scopeKey(b);

/** The library URL for a scope (and section / only-here); Everything has no scope param. */
export function libraryHref(s: LibScope, opts: { section?: LibSection | ""; here?: boolean } = {}): string {
  const p = new URLSearchParams();
  if (s.level !== "account") p.set("scope", scopeKey(s));
  if (opts.here && s.level !== "account") p.set("here", "1");
  if (opts.section) p.set("s", opts.section);
  const q = p.toString();
  return q ? `/library?${q}` : "/library";
}

/** The api's upload scope ({channelId} / {collectionId} / {projectId}); Everything: none (the account level). */
export function uploadScope(s: LibScope): { channelId?: string; collectionId?: string; projectId?: string } | undefined {
  if (s.level === "channel") return { channelId: s.id };
  if (s.level === "collection") return { collectionId: s.id };
  if (s.level === "project") return { projectId: s.id };
  return undefined;
}

/** Where an item is attached: the most specific level (the api sends `scope`; older items are read from their ids). */
export function itemScope(a: Pick<Asset, "scope" | "projectId" | "collectionId" | "channelId">): LibScope {
  if (a.scope) return parseScope(a.scope);
  if (a.projectId) return { level: "project", id: a.projectId };
  if (a.collectionId) return { level: "collection", id: a.collectionId };
  if (a.channelId) return { level: "channel", id: a.channelId };
  return EVERYTHING;
}

/** What the tree knows: a video's channel and collection, a collection's channel. */
export type ScopeTree = {
  projects: Record<string, { channelId: string | null; collectionId?: string | null }>;
  collections: Record<string, { channelId: string }>;
};

/**
 * The api's scope rule (uploads.ts libraryFilter): a scope shows everything at that level and below (a channel: its
 * own items, its collections' and its videos'); `here` only what's attached at that level itself.
 */
export function inScope(a: Asset, s: LibScope, here: boolean, tree: ScopeTree): boolean {
  const at = itemScope(a);
  if (s.level === "account") return !here || at.level === "account";
  if (here) return sameScope(at, s);
  if (s.level === "project") return a.projectId === s.id;
  if (s.level === "collection") return a.collectionId === s.id || (!!a.projectId && tree.projects[a.projectId]?.collectionId === s.id);
  // channel
  if (a.channelId === s.id) return true;
  if (a.collectionId && tree.collections[a.collectionId]?.channelId === s.id) return true;
  return !!a.projectId && tree.projects[a.projectId]?.channelId === s.id;
}

export type ScopeCrumb = { scope: LibScope; label: string };
export type ScopeNames = {
  channels: { id: string; name: string }[];
  collections: { id: string; name: string; channelId: string }[];
  projects: { id: string; title: string; channelId: string | null }[];
  /** a video's collection, when known (GET /projects/:id/settings) */
  projectCollection?: string | null;
};

/** Everything › Kettle & Crumb › Milo & Friends › Pip and the Dark: the levels above (and at) a scope. */
export function scopeChain(s: LibScope, n: ScopeNames): ScopeCrumb[] {
  const out: ScopeCrumb[] = [{ scope: EVERYTHING, label: "Everything" }];
  const ch = (id: string | null | undefined) => {
    const c = id ? n.channels.find((x) => x.id === id) : undefined;
    if (c) out.push({ scope: { level: "channel", id: c.id }, label: c.name });
  };
  const col = (id: string | null | undefined) => {
    const c = id ? n.collections.find((x) => x.id === id) : undefined;
    if (!c) return false;
    ch(c.channelId);
    out.push({ scope: { level: "collection", id: c.id }, label: c.name });
    return true;
  };
  if (s.level === "channel") {
    ch(s.id);
    if (out.length === 1) out.push({ scope: s, label: "This channel" });
  } else if (s.level === "collection") {
    if (!col(s.id)) out.push({ scope: s, label: "This collection" });
  } else if (s.level === "project") {
    const p = n.projects.find((x) => x.id === s.id);
    if (!col(n.projectCollection)) ch(p?.channelId);
    out.push({ scope: s, label: p?.title || "This video" });
  }
  return out;
}

/** The short name of a scope ("Milo & Friends", "your library"). */
export function scopeName(s: LibScope, n: ScopeNames): string {
  if (s.level === "account") return "your library";
  return scopeChain(s, n).at(-1)!.label;
}

/** Every level an item can live at, for "Where it lives" (the pickers group them). */
export function scopeOptions(n: ScopeNames): { group: string; options: { key: string; label: string }[] }[] {
  const groups = [
    { group: "", options: [{ key: "account", label: "Everything (not tied to anything)" }] },
    { group: "Channels", options: n.channels.map((c) => ({ key: `channel:${c.id}`, label: c.name })) },
    { group: "Collections", options: n.collections.map((c) => ({ key: `collection:${c.id}`, label: c.name })) },
    { group: "Videos", options: n.projects.map((p) => ({ key: `project:${p.id}`, label: p.title || "Untitled video" })) },
  ];
  return groups.filter((g) => g.options.length);
}

// ------------------------------------------------------------------ filters

export type LibFilter = { section?: LibSection | ""; q?: string; channelId?: string; projectId?: string };

const time = (a: Asset) => (a.createdAt ? new Date(a.createdAt).getTime() || 0 : 0);
export const descriptionOf = (a: Asset): string => (typeof a.meta.description === "string" ? a.meta.description : "");

/** Filter by section, brand, project and words (name, your tags, auto tags, description), newest first. */
export function filterLibrary(items: Asset[], f: LibFilter): Asset[] {
  const words = (f.q ?? "").toLowerCase().split(/\s+/).filter(Boolean);
  return items
    .filter((a) => !f.section || sectionOf(a) === f.section)
    .filter((a) => !f.channelId || a.channelId === f.channelId)
    .filter((a) => !f.projectId || a.projectId === f.projectId)
    .filter((a) => {
      if (!words.length) return true;
      const hay = [a.name, ...a.tags, ...(a.autoTags ?? []), descriptionOf(a)].join(" ").toLowerCase();
      return words.every((w) => hay.includes(w));
    })
    .slice()
    .sort((a, b) => time(b) - time(a));
}

// ------------------------------------------------------------------ roles and tags (spec-v4 7.1)

/** The roles a user can give each kind (api uploads.ts USER_ROLES), with their words. */
export const USER_ROLES: Partial<Record<Asset["kind"], { role: string; label: string }[]>> = {
  image: [
    { role: "product", label: "Product" },
    { role: "location", label: "Place" },
    { role: "prop", label: "Prop" },
    { role: "photo", label: "Photo" },
    { role: "drawing", label: "Drawing" },
  ],
  music: [
    { role: "song", label: "Song" },
    { role: "theme", label: "Theme music" },
    { role: "music", label: "Music bed" },
  ],
  video: [
    { role: "intro", label: "Intro" },
    { role: "outro", label: "Outro" },
  ],
};
export const roleLabel = (kind: Asset["kind"], role: string | null | undefined) => (role ? (USER_ROLES[kind]?.find((r) => r.role === role)?.label ?? role) : "");

/** A typed tag list ("warm, Kitchen,  warm") -> clean, lower-case, no repeats, at most 20 of 40 characters. */
export function cleanTags(input: string | string[]): string[] {
  const parts = (Array.isArray(input) ? input : input.split(",")).map((t) => t.trim().toLowerCase().slice(0, 40)).filter(Boolean);
  return [...new Set(parts)].slice(0, 20);
}

// ------------------------------------------------------------------ the upload sheet

export type UploadKind = "image" | "logo" | "music" | "sfx" | "video";
export type Media = "image" | "audio" | "video";
/** What the user says a file is: picks the api's kind + role. */
export type UploadWhat = "picture" | "product" | "place" | "prop" | "drawing" | "person" | "logo" | "song" | "theme" | "sfx" | "clip" | "intro" | "outro";
export const WHATS: Record<UploadWhat, { label: string; kind: UploadKind; role: string | null; media: Media }> = {
  picture: { label: "Picture", kind: "image", role: null, media: "image" },
  product: { label: "Product photo", kind: "image", role: "product", media: "image" },
  place: { label: "Place", kind: "image", role: "location", media: "image" },
  prop: { label: "Prop", kind: "image", role: "prop", media: "image" },
  drawing: { label: "Drawing", kind: "image", role: "drawing", media: "image" },
  person: { label: "Photo of a person", kind: "image", role: "photo", media: "image" },
  logo: { label: "Logo", kind: "logo", role: null, media: "image" },
  song: { label: "Song", kind: "music", role: "song", media: "audio" },
  theme: { label: "Theme music", kind: "music", role: "theme", media: "audio" },
  sfx: { label: "Sound effect", kind: "sfx", role: null, media: "audio" },
  clip: { label: "Clip", kind: "video", role: null, media: "video" },
  intro: { label: "Intro", kind: "video", role: "intro", media: "video" },
  outro: { label: "Outro", kind: "video", role: "outro", media: "video" },
};
export const whatsFor = (m: Media) => (Object.keys(WHATS) as UploadWhat[]).filter((w) => WHATS[w].media === m);

export type UploadLimit = { mimes: string[]; maxBytes: number; maxSeconds?: number };
const MB = 1024 * 1024;
const PICTURES = ["image/png", "image/jpeg", "image/webp"];
/** api config.ts UPLOAD_LIMITS (spec-v4 7.2), used until GET /api/me/storage answers with its own. */
export const DEFAULT_LIMITS: Record<UploadKind, UploadLimit> = {
  image: { mimes: PICTURES, maxBytes: 20 * MB },
  logo: { mimes: PICTURES, maxBytes: 5 * MB },
  music: { mimes: ["audio/mpeg", "audio/wav", "audio/mp4", "audio/aac"], maxBytes: 100 * MB, maxSeconds: 10 * 60 },
  sfx: { mimes: ["audio/mpeg", "audio/wav"], maxBytes: 20 * MB, maxSeconds: 60 },
  video: { mimes: ["video/mp4", "video/quicktime"], maxBytes: 500 * MB, maxSeconds: 5 * 60 },
};
export const INTRO_OUTRO_SECONDS = 10;
export const DEFAULT_QUOTA_BYTES = 5 * 1024 * MB;

const TYPE_NAMES: Record<UploadKind, string> = { image: "PNG, JPEG or WebP", logo: "PNG, JPEG or WebP", music: "MP3, WAV or M4A", sfx: "MP3 or WAV", video: "MP4 or MOV" };
const KIND_PLURAL: Record<UploadKind, string> = { image: "Pictures", logo: "Logos", music: "Songs", sfx: "Sound effects", video: "Clips" };

const BY_EXT: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heic",
  gif: "image/gif",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  m4a: "audio/mp4",
  aac: "audio/aac",
  ogg: "audio/ogg",
  flac: "audio/flac",
  mp4: "video/mp4",
  m4v: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
};
const ALIASES: Record<string, string> = {
  "image/jpg": "image/jpeg",
  "audio/mp3": "audio/mpeg",
  "audio/x-wav": "audio/wav",
  "audio/wave": "audio/wav",
  "audio/x-m4a": "audio/mp4",
  "audio/m4a": "audio/mp4",
  "audio/x-aac": "audio/aac",
  "video/x-m4v": "video/mp4",
};
type FileLike = { name: string; type: string; size: number };

/** The file's type (what the browser said, else its extension; some phones send none for audio). */
export function mimeOf(f: Pick<FileLike, "name" | "type">): string {
  const m = f.type.trim().toLowerCase();
  if (m && m !== "application/octet-stream") return ALIASES[m] ?? m;
  return BY_EXT[/\.([a-z0-9]+)$/i.exec(f.name)?.[1]?.toLowerCase() ?? ""] ?? "";
}
export function mediaOf(f: Pick<FileLike, "name" | "type">): Media | null {
  const m = mimeOf(f);
  return m.startsWith("image/") ? "image" : m.startsWith("audio/") ? "audio" : m.startsWith("video/") ? "video" : null;
}

const SFX_WORDS = /\b(sfx|fx|effect|whoosh|swoosh|swish|hit|click|boom|ding|creak|knock|pop|thud|beep|chime|riser|impact)\b/i;
const LOGO_WORDS = /\blogo\b/i;

/**
 * What a picked file most likely is: the sheet's preset when it's the same sort of media ("Add an intro" stays an
 * intro), else a picture, a song (a sound effect when the name says so) or a clip. Null: not a file we take.
 */
export function guessWhat(f: Pick<FileLike, "name" | "type">, preset?: UploadWhat | null): UploadWhat | null {
  const m = mediaOf(f);
  if (!m) return null;
  if (preset && WHATS[preset].media === m) return preset;
  const words = f.name.replace(/[_.-]+/g, " ");
  if (m === "image") return LOGO_WORDS.test(words) ? "logo" : "picture";
  if (m === "audio") return SFX_WORDS.test(words) ? "sfx" : "song";
  return "clip";
}

/** The longest a file of this kind may play (intro and outro clips stay short). */
export function maxSecondsOf(what: UploadWhat, limits: Record<UploadKind, UploadLimit> = DEFAULT_LIMITS): number | undefined {
  if (what === "intro" || what === "outro") return INTRO_OUTRO_SECONDS;
  return limits[WHATS[what].kind].maxSeconds;
}

const mbText = (b: number) => (b >= 1024 * MB ? `${+(b / (1024 * MB)).toFixed(1)} GB` : `${Math.round(b / MB)} MB`);
export const timeText = (s: number) => (s >= 60 ? `${Math.round(s / 60)} minute${Math.round(s / 60) === 1 ? "" : "s"}` : `${Math.round(s)} seconds`);
/** "A picture", "An intro", "Theme music" (how a sentence about one file starts). */
export function aWhat(what: UploadWhat): string {
  if (what === "theme") return "Theme music";
  const w = WHATS[what].label.toLowerCase();
  return `${/^[aeiou]/.test(w) ? "An" : "A"} ${w}`;
}

/** Checks a file before a byte moves: a type we take, and under the size for what it is. Null when it's fine. */
export function checkFile(f: FileLike, what: UploadWhat, limits: Record<UploadKind, UploadLimit> = DEFAULT_LIMITS): string | null {
  const w = WHATS[what];
  const lim = limits[w.kind];
  const mime = mimeOf(f);
  if (mime === "image/heic") return "That's a HEIC photo. Share it from your phone as a JPEG, or take a screenshot, and add that.";
  if (!lim.mimes.includes(mime)) return `${KIND_PLURAL[w.kind]} have to be ${TYPE_NAMES[w.kind]}.`;
  if (f.size > lim.maxBytes) return `It's ${fmtBytes(f.size)}. ${aWhat(what)} can be up to ${mbText(lim.maxBytes)}.`;
  if (f.size < 1) return "That file is empty.";
  return null;
}

/** The api's error on an upload, in plain words for the file's row. */
export function uploadErrorText(err: { status?: number; code?: string | null; message?: string; details?: unknown }, what: UploadWhat, limits: Record<UploadKind, UploadLimit> = DEFAULT_LIMITS): string {
  const w = WHATS[what];
  const d = (err.details && typeof err.details === "object" ? err.details : {}) as Record<string, unknown>;
  const a = aWhat(what);
  switch (err.code) {
    case "TERMS_NEEDED":
      return "Tick the box above to say you own these files, then try again.";
    case "QUOTA_EXCEEDED": {
      const quota = typeof d.quotaBytes === "number" ? d.quotaBytes : DEFAULT_QUOTA_BYTES;
      const used = typeof d.usedBytes === "number" ? d.usedBytes : null;
      return `That would go past your ${mbText(quota)} of uploads${used !== null ? ` (${fmtBytes(Math.max(0, quota - used)) || "nothing"} left)` : ""}. Delete something you uploaded to make room.`;
    }
    case "TOO_LARGE":
      return `It's too big. ${a} can be up to ${mbText(typeof d.maxBytes === "number" ? d.maxBytes : limits[w.kind].maxBytes)}.`;
    case "TOO_LONG": {
      const max = typeof d.maxSeconds === "number" ? d.maxSeconds : maxSecondsOf(what, limits);
      const len = typeof d.durationS === "number" ? d.durationS : null;
      return `${len !== null ? `It's ${fmtClock(len)} long. ` : "It's too long. "}${a} can be up to ${max ? timeText(max) : "a few minutes"}.`;
    }
    case "UNSUPPORTED_TYPE":
      return `${KIND_PLURAL[w.kind]} have to be ${TYPE_NAMES[w.kind]}.`;
    case "BAD_FILE":
      return "That file doesn't open. It may be damaged, or not what its name says.";
    case "RATE_LIMITED":
      return err.message || "That's a lot of uploads at once. Try again a little later.";
  }
  if (err.status === 0) return "The upload didn't go through. Check your connection and try again.";
  return err.message || "Couldn't upload it. Try again.";
}

/** "1:05" for a clock reading; "12:30" past ten minutes. */
export function fmtClock(s: number): string {
  return fmtLength(s);
}

/** "Pictures 20 MB · Songs 100 MB · Clips 500 MB" (the upload sheet's limits line). */
export function limitsLine(limits: Record<UploadKind, UploadLimit> = DEFAULT_LIMITS): string {
  return (["image", "music", "sfx", "video"] as UploadKind[]).map((k) => `${KIND_PLURAL[k]} ${mbText(limits[k].maxBytes)}`).join(" · ");
}

/** The storage meter: "1.2 of 5 GB used", the share used (0..1) and how close to full. A null quota (an internal
 *  account) has no limit. Only uploads count; what the Director makes is free to keep. */
export function storageMeter(usedBytes: number, quotaBytes: number | null): { text: string; share: number; tone: "ok" | "near" | "full" } {
  if (quotaBytes === null || quotaBytes <= 0) return { text: `${fmtBytes(usedBytes) || "0 B"} of uploads, no limit`, share: 0, tone: "ok" };
  const share = Math.max(0, Math.min(1, usedBytes / quotaBytes));
  const gb = quotaBytes >= 1024 * MB;
  const used = gb ? `${+(usedBytes / (1024 * MB)).toFixed(usedBytes < 100 * MB ? 2 : 1)}` : `${Math.round(usedBytes / MB)}`;
  return { text: `${used === "0" ? "0" : used} of ${mbText(quotaBytes)} used`, share, tone: share >= 0.98 ? "full" : share >= 0.85 ? "near" : "ok" };
}

/** Whether a batch fits what's left; the message names how much room there is. */
export function quotaCheck(incoming: number, usedBytes: number, quotaBytes: number | null): string | null {
  if (quotaBytes === null || usedBytes + incoming <= quotaBytes) return null;
  const left = Math.max(0, quotaBytes - usedBytes);
  return `These come to ${fmtBytes(incoming)} and you have ${fmtBytes(left) || "no room"} left of your ${mbText(quotaBytes)}. Delete something you uploaded, or add fewer files.`;
}

/** The line under a finished upload: "tagged: warm, acoustic · 2:41". */
export function doneLine(a: Pick<Asset, "autoTags" | "tags" | "meta">): string {
  const tags = [...(a.autoTags ?? []), ...a.tags].slice(0, 4);
  const d = durationOf(a as Asset);
  return [tags.length ? `tagged: ${tags.join(", ")}` : "tagging…", d && fmtLength(d)].filter(Boolean).join(" · ");
}

// ------------------------------------------------------------------ meta

export const durationOf = (a: Asset): number | undefined => {
  const d = a.meta.durationS ?? a.meta.duration_s;
  return typeof d === "number" && d > 0 ? d : undefined;
};
export const versionOf = (a: Asset): number | undefined => {
  const v = a.meta.version;
  return typeof v === "number" && v > 0 ? v : undefined;
};

/** "0:30" */
export function fmtLength(s: number): string {
  const t = Math.round(s);
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
}

/** A moment in a video to a tenth of a second, "0:07.4" (Change thumbnail's scrubber). */
export function fmtFrameTime(s: number): string {
  const t = Math.round(Math.max(0, s) * 10) / 10;
  const m = Math.floor(t / 60);
  return `${m}:${(t - m * 60).toFixed(1).padStart(4, "0")}`;
}

/** Whether the full-screen player can show it (anything with a URL; audio plays over its waveform). */
export const viewable = (a: Asset) => !!a.url;

/** The next or previous index in the player's list; stays put at the ends. */
export function stepIndex(i: number, n: number, dir: "next" | "prev"): number {
  if (n <= 0) return 0;
  return Math.max(0, Math.min(n - 1, i + (dir === "next" ? 1 : -1)));
}

/**
 * A vertical swipe on the full-screen player: up = next, down = previous. Needs a mostly vertical move of 60 px,
 * or a quick 30 px flick (under 250 ms).
 */
export function swipeDir(dx: number, dy: number, ms: number): "next" | "prev" | null {
  const ady = Math.abs(dy);
  if (ady < Math.abs(dx) * 1.2) return null;
  if (ady >= 60 || (ady >= 30 && ms < 250)) return dy < 0 ? "next" : "prev";
  return null;
}
